import React, { useState, useRef, useEffect } from "react";
import { vaultConfig, vaultCatalogs } from "../data/mock";
import { useAudioPlayer } from "../hooks/useAudioPlayer";
import { Lock, Unlock, ShieldCheck, AlertTriangle, Music, ArrowLeft, Play, Pause, Download, Volume2, VolumeX, CheckCircle, Briefcase, Tv, Skull, Zap, Mic, Sun } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Slider } from "../components/ui/slider";
import { Link } from "react-router-dom";

// ═══════════════════════════════════════════════════════════════════════════════
// B2B SUPERVISOR VAULT - ITZGREEN MUSIC
// Organized by TV Use-Case for rapid supervisor deployment
// Legal Status: 100% Human-Produced | One-Stop Cleared (BMI/The MLC)
// ═══════════════════════════════════════════════════════════════════════════════

// Vault catalogs data moved to ../data/mock.js

// Folder icon mapping
const FolderIcon = ({ icon, size = 18 }) => {
  const icons = {
    Tv: Tv,
    Zap: Zap,
    Skull: Skull,
    Briefcase: Briefcase,
    Mic: Mic,
    Sun: Sun
  };
  const IconComponent = icons[icon] || Music;
  return <IconComponent size={size} />;
};

// Premium Audio Player Component
const AudioPlayer = ({ track, isActive, onPlay, onPause, globalAudioRef }) => {
  const {
    audioRef,
    progressRef,
    currentTime,
    duration,
    volume,
    isMuted,
    setVolume,
    setIsMuted,
    handleTimeUpdate,
    handleLoadedMetadata,
    handleProgressClick,
    handlePlayToggle,
    formatTime,
    progress,
    hasAudio
  } = useAudioPlayer(track.streamUrl, isActive, onPlay, onPause, globalAudioRef);

  return (
    <div className="space-y-3">
      {/* Hidden Audio Element */}
      {hasAudio && (
        <audio
          ref={audioRef}
          src={track.streamUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={onPause}
          preload="metadata"
        />
      )}

      {/* Main Player Row */}
      <div className="flex items-center gap-3 sm:gap-4">
        {/* Play Button */}
        <button
          onClick={handlePlayToggle}
          disabled={!hasAudio}
          data-testid={`play-btn-${track.id}`}
          className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
            !hasAudio 
              ? "bg-[rgb(40,40,45)] text-[rgb(80,80,90)] cursor-not-allowed"
              : isActive 
                ? "bg-[rgb(218,255,1)] text-[rgb(17,17,19)] shadow-[0_0_20px_rgba(218,255,1,0.4)]" 
                : "bg-[rgba(218,255,1,0.1)] text-[rgb(218,255,1)] hover:bg-[rgba(218,255,1,0.25)] hover:shadow-[0_0_15px_rgba(218,255,1,0.2)]"
          }`}
        >
          {isActive ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
        </button>

        {/* Progress Bar & Time */}
        <div className="flex-1 min-w-0">
          <div 
            ref={progressRef}
            onClick={handleProgressClick}
            className={`relative h-1.5 bg-[rgb(40,40,45)] rounded-full overflow-hidden ${hasAudio ? 'cursor-pointer group' : 'cursor-default'}`}
          >
            {/* Progress Fill */}
            <motion.div 
              className="absolute h-full bg-gradient-to-r from-[rgb(218,255,1)] to-[rgb(180,220,0)] rounded-full"
              style={{ width: `${progress}%` }}
              initial={false}
              transition={{ duration: 0.1 }}
            />
            {/* Hover Indicator */}
            {hasAudio && (
              <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity" 
                style={{ left: `calc(${progress}% - 6px)` }} 
              />
            )}
          </div>
          
          {/* Time Display */}
          <div className="flex justify-between mt-1.5">
            <span className="text-[rgb(120,120,130)] text-[10px] font-mono">
              {hasAudio ? formatTime(currentTime) : "--:--"}
            </span>
            <span className="text-[rgb(120,120,130)] text-[10px] font-mono">
              {hasAudio ? formatTime(duration) : track.duration || "--:--"}
            </span>
          </div>
        </div>

        {/* Volume Control - Desktop Only */}
        <div className="hidden lg:flex items-center gap-2">
          <button
            onClick={() => setIsMuted(!isMuted)}
            disabled={!hasAudio}
            className={`p-1.5 rounded transition-colors ${
              !hasAudio 
                ? "text-[rgb(60,60,70)] cursor-not-allowed" 
                : "text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)]"
            }`}
          >
            {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <div className="w-20">
            <Slider
              value={[isMuted ? 0 : volume * 100]}
              onValueChange={(val) => {
                setVolume(val[0] / 100);
                if (val[0] > 0) setIsMuted(false);
              }}
              max={100}
              step={1}
              disabled={!hasAudio}
              className="cursor-pointer"
            />
          </div>
        </div>
      </div>

      {/* "Audio Coming Soon" Indicator */}
      {!hasAudio && (
        <div className="flex items-center gap-2 pl-14 sm:pl-16">
          <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
          <span className="text-amber-500/80 text-[10px] tracking-wider font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
            STREAMING AVAILABLE SOON
          </span>
        </div>
      )}
    </div>
  );
};

// Track Component with Premium Player - B2B Supervisor Optimized
const TrackRow = ({ track, index, activeTrackId, setActiveTrackId, globalAudioRef }) => {
  const isActive = activeTrackId === track.id;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.08 }}
      data-testid={`track-row-${track.id}`}
      className={`group bg-[rgb(26,28,30)] border rounded-xl p-4 sm:p-5 transition-all duration-300 ${
        isActive 
          ? "border-[rgba(218,255,1,0.5)] shadow-[0_0_30px_rgba(218,255,1,0.1)]" 
          : "border-[rgba(255,255,255,0.08)] hover:border-[rgba(218,255,1,0.3)]"
      }`}
    >
      {/* Track Header */}
      <div className="flex items-start sm:items-center justify-between gap-4 mb-4">
        {/* Track Info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4
              className="text-white text-sm sm:text-base font-semibold tracking-wide truncate"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              {track.title}
            </h4>
            {track.mix && (
              <span className="text-[rgb(218,255,1)] text-[10px] font-medium px-2 py-0.5 bg-[rgba(218,255,1,0.1)] rounded">
                {track.mix}
              </span>
            )}
          </div>
          
          {/* Technical Metadata Row */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {/* Genre Tag - Searchability */}
            {track.genre && (
              <span 
                className="text-[rgb(161,161,170)] text-[10px] sm:text-xs font-medium"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {track.genre}
              </span>
            )}
            {/* BPM & Key - Technical */}
            <span 
              className="text-[rgb(100,100,110)] text-[10px] sm:text-xs"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {track.bpm} BPM • {track.key}
            </span>
            {/* Duration */}
            <span 
              className="text-[rgb(80,80,90)] text-[10px]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {track.duration}
            </span>
          </div>
        </div>

        {/* Right: ONE-STOP BADGE (Most Prominent) + BMI */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {/* ONE-STOP CLEARED BADGE - THE LIABILITY SHIELD */}
          <div className="flex items-center gap-1.5 bg-[rgba(74,222,128,0.1)] border border-[rgba(74,222,128,0.3)] rounded-lg px-2 sm:px-3 py-1.5">
            <ShieldCheck size={14} className="text-[rgb(74,222,128)]" />
            <span 
              className="text-[rgb(74,222,128)] text-[9px] sm:text-[10px] font-bold tracking-wider" 
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              ONE-STOP
            </span>
          </div>
          
          {/* BMI Work No */}
          {track.bmiWorkNo && (
            <span className="hidden lg:inline text-[rgb(100,100,110)] text-[10px] font-mono">
              BMI: {track.bmiWorkNo}
            </span>
          )}
        </div>
      </div>

      {/* Premium Audio Player */}
      <AudioPlayer 
        track={track}
        isActive={isActive}
        onPlay={() => setActiveTrackId(track.id)}
        onPause={() => setActiveTrackId(null)}
        globalAudioRef={globalAudioRef}
      />

      {/* Mix Versions Panel (for Q2 standardized assets) */}
      {track.versions && track.versions.length > 0 && (
        <div className="mt-4 border-t border-[rgba(255,255,255,0.06)] pt-4">
          <p className="text-[10px] text-[rgb(100,100,110)] tracking-[0.15em] font-bold mb-3">ADOBE PREMIERE DELIVERABLES</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {track.versions.map((v, vi) => (
              <div key={vi} className={`flex items-center justify-between px-3 py-2 rounded-lg text-[11px] font-mono ${
                v.status === 'LIVE' 
                  ? 'bg-[rgba(74,222,128,0.06)] border border-[rgba(74,222,128,0.2)]'
                  : 'bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.06)]'
              }`} data-testid={`version-${track.id}-${v.mix.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${v.status === 'LIVE' ? 'bg-[rgb(74,222,128)]' : 'bg-[rgb(80,80,90)]'}`} />
                  <span className="text-white truncate">{v.mix}</span>
                </div>
                <span className={`text-[9px] tracking-wider font-bold flex-shrink-0 ml-2 ${
                  v.status === 'LIVE' ? 'text-[rgb(74,222,128)]' : 'text-[rgb(80,80,90)]'
                }`}>{v.status}</span>
              </div>
            ))}
          </div>
          {track.versions.some(v => v.status === 'AWAITING DELIVERY') && (
            <p className="text-[9px] text-[rgb(80,80,90)] mt-2 italic">
              Pending versions will be activated upon Nijay Scott delivery.
            </p>
          )}
        </div>
      )}
      
      {/* Mobile: BMI Reference */}
      {track.bmiWorkNo && (
        <div className="flex lg:hidden items-center justify-end mt-3">
          <span className="text-[rgb(100,100,110)] text-[9px] font-mono">
            BMI: {track.bmiWorkNo}
          </span>
        </div>
      )}
    </motion.div>
  );
};

// Catalog Section Component - B2B Folder Structure
const CatalogSection = ({ catalog, index, activeTrackId, setActiveTrackId, globalAudioRef }) => {
  const isBespoke = catalog.isPitchPackage;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 + index * 0.15 }}
      className={`mb-12 sm:mb-16 rounded-2xl transition-all duration-300 ${
        isBespoke 
          ? "p-6 sm:p-8 border-2 border-[rgb(218,255,1)] bg-[rgba(218,255,1,0.03)] shadow-[0_0_50px_rgba(218,255,1,0.05)] relative overflow-hidden" 
          : ""
      }`}
      data-testid={`catalog-section-${index}`}
    >
      {/* Bespoke Background Accents */}
      {isBespoke && (
        <div className="absolute top-0 right-0 p-4 opacity-20 pointer-events-none">
          <Briefcase size={120} className="text-[rgb(218,255,1)] -rotate-12 translate-x-10 -translate-y-10" />
        </div>
      )}

      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5 sm:mb-6 relative z-10">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              isBespoke ? "bg-[rgb(218,255,1)] text-[rgb(17,17,19)]" : "bg-[rgba(218,255,1,0.1)] text-[rgb(218,255,1)]"
            }`}>
              <FolderIcon icon={catalog.icon} size={16} />
            </div>
            <h2
              className={`text-lg sm:text-xl font-bold ${isBespoke ? "text-white" : "text-[rgb(218,255,1)]"}`}
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              {catalog.title}
            </h2>
            {/* BESPOKE BADGE */}
            {isBespoke && (
              <Badge 
                className="bg-[rgb(218,255,1)] text-[rgb(17,17,19)] hover:bg-[rgb(166,190,21)] border-none text-[10px] font-bold tracking-widest px-2 py-0.5"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                BESPOKE PITCH
              </Badge>
            )}
          </div>
          <p
            className="text-white text-xs sm:text-sm font-medium tracking-wide pl-11 mb-1"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {catalog.subtitle}
          </p>
          <p
            className="text-[rgb(161,161,170)] text-xs pl-11 mb-2"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {catalog.vibe}
          </p>
          {/* Pitch Specific Info */}
          {isBespoke && catalog.briefRef && (
            <div className="pl-11 mb-2">
              <span className="text-[rgb(218,255,1)] text-[10px] font-mono bg-[rgba(218,255,1,0.1)] px-2 py-1 rounded">
                REF: {catalog.briefRef}
              </span>
            </div>
          )}
          {/* Producer Credit - Legal Name Format */}
          <p
            className="text-[rgb(100,100,110)] text-[10px] tracking-[0.1em] pl-11"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Active Roster: <span className="text-[rgb(161,161,170)]">{catalog.producer}</span>
          </p>
          {/* Embedded Metadata Shield */}
          {catalog.metadata && (
            <div className="pl-11 mt-3 flex flex-wrap gap-2">
              <span className="text-[9px] bg-[rgba(74,222,128,0.08)] border border-[rgba(74,222,128,0.2)] text-[rgb(74,222,128)] px-2 py-1 rounded font-mono">
                {catalog.metadata.clearance}
              </span>
              <span className="text-[9px] bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-[rgb(161,161,170)] px-2 py-1 rounded font-mono">
                {catalog.metadata.composer}
              </span>
              {catalog.metadata.publisher && (
                <span className="text-[9px] bg-[rgba(218,255,1,0.06)] border border-[rgba(218,255,1,0.15)] text-[rgb(218,255,1)] px-2 py-1 rounded font-mono">
                  {catalog.metadata.publisher}
                </span>
              )}
              {catalog.metadata.syncAdmin && (
                <span className="text-[9px] bg-[rgba(74,222,128,0.05)] border border-[rgba(74,222,128,0.12)] text-[rgb(74,222,128)] px-2 py-1 rounded font-mono italic">
                  {catalog.metadata.syncAdmin}
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* Section Download Button - only show if downloadUrl exists */}
        {catalog.downloadUrl && (
          <a
            href={catalog.downloadUrl}
            download
            data-testid={`download-collection-${index}`}
            className="inline-flex items-center gap-2 bg-[rgb(63,63,63)] text-white text-xs font-semibold tracking-[0.08em] px-4 py-2.5 rounded-lg hover:bg-[rgb(218,255,1)] hover:text-[rgb(17,17,19)] transition-all duration-300 w-fit"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <Download size={14} />
            DOWNLOAD COLLECTION
          </a>
        )}
      </div>

      {/* Tracks */}
      {catalog.tracks.length > 0 ? (
        <div className="space-y-3 sm:space-y-4 relative z-10">
          {catalog.tracks.map((track, i) => (
            <TrackRow 
              key={track.id} 
              track={track} 
              index={i} 
              activeTrackId={activeTrackId}
              setActiveTrackId={setActiveTrackId}
              globalAudioRef={globalAudioRef}
            />
          ))}
        </div>
      ) : (
        <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-6 text-center">
          <p className="text-[rgb(161,161,170)] text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
            Tracks coming soon
          </p>
        </div>
      )}
    </motion.div>
  );
};

const VaultPage = () => {
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [activeTrackId, setActiveTrackId] = useState(null);
  const globalAudioRef = useRef(null);

  // Check for pre-authentication from homepage quick-access
  useEffect(() => {
    const preAuth = sessionStorage.getItem("vaultAuthenticated");
    if (preAuth === "true") {
      setUnlocked(true);
      sessionStorage.removeItem("vaultAuthenticated"); // Clear after use
    }
  }, []);

  const handleUnlock = (e) => {
    e.preventDefault();
    if (password.toUpperCase() === vaultConfig.password.toUpperCase()) {
      setUnlocked(true);
      setError(false);
    } else {
      setError(true);
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <main className="min-h-screen bg-[rgb(17,17,19)] pt-28 pb-20 relative overflow-hidden">
      {/* Background grid */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="max-w-[1000px] mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[rgb(161,161,170)] text-xs tracking-[0.2em] hover:text-[rgb(218,255,1)] transition-colors mb-10 sm:mb-12"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <ArrowLeft size={14} />
          BACK TO PORTAL
        </Link>

        <AnimatePresence mode="wait">
          {!unlocked ? (
            /* ═══════════════ THE GATE (LOGIN SCREEN) ═══════════════ */
            <motion.div
              key="locked"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex flex-col items-center text-center max-w-md mx-auto"
            >
              {/* Lock Icon */}
              <motion.div 
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-[rgb(26,28,30)] border-2 border-[rgb(63,63,63)] flex items-center justify-center mb-8"
                animate={{ 
                  boxShadow: ["0 0 0px rgba(218,255,1,0)", "0 0 30px rgba(218,255,1,0.15)", "0 0 0px rgba(218,255,1,0)"]
                }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <Lock size={36} className="text-[rgb(218,255,1)]" />
              </motion.div>

              <Badge
                variant="outline"
                className="border-[rgb(218,255,1)] text-[rgb(218,255,1)] bg-[rgba(218,255,1,0.05)] mb-6 px-4 py-1.5 text-[10px] sm:text-xs tracking-[0.2em] rounded-md"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                RESTRICTED ACCESS
              </Badge>

              <h1
                className="text-white text-3xl sm:text-4xl md:text-5xl font-bold mb-3"
                style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.02em" }}
              >
                ITZGREEN MUSIC: B2B SYNC INFRASTRUCTURE
              </h1>
              <p
                className="text-[rgb(218,255,1)] text-xs sm:text-sm tracking-[0.2em] mb-4 font-bold"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                THE 100% HUMAN-PRODUCED LIABILITY SHIELD
              </p>
              
              <div className="max-w-xl mx-auto text-left space-y-4 mb-10 bg-[rgba(26,28,30,0.8)] p-6 rounded-xl border border-[rgba(63,63,63,0.5)]">
                <p className="text-[rgb(200,200,200)] text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  ItzGreen Music provides a secure, frictionless audio pipeline for premium episodic television, trailer houses, and global brand campaigns.
                </p>
                <div className="space-y-2">
                  <p className="text-[rgb(218,255,1)] text-xs font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>THE B2B VAULT GUARANTEE:</p>
                  <ul className="text-[rgb(161,161,170)] text-[11px] space-y-1 list-disc pl-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                    <li><span className="text-white font-medium">100% Human-Produced:</span> Zero AI generation in our sync catalog.</li>
                    <li><span className="text-white font-medium">Zero Uncleared Samples:</span> Every asset is built from the ground up.</li>
                    <li><span className="text-white font-medium">One-Stop Clearance:</span> Master and Publishing strictly controlled in-house via Vault Sync Publishing.</li>
                    <li><span className="text-white font-medium">Fully Reconciled:</span> ASCAP / BMI registered and indemnified.</li>
                  </ul>
                </div>
                <p className="text-[rgb(200,200,200)] text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  We supply high-tension Corporate Drill, Cinematic Industrial, and aggressive R&B stems designed for post-production editors who require pristine, uncompressed 24-bit audio without the legal friction.
                </p>
              </div>

              <p
                className="text-[rgb(161,161,170)] text-[10px] tracking-[0.2em] mb-4"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                ACCESS THE SUPERVISOR VAULT<br/>(Authorized Agency and Studio Personnel Only)
              </p>

              {/* Password Form */}
              <form onSubmit={handleUnlock} className="w-full space-y-4">
                <div className="relative">
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter access code"
                    className={`bg-[rgb(26,28,30)] border-2 text-white placeholder:text-[rgb(100,100,110)] rounded-xl h-14 text-center text-lg tracking-[0.25em] transition-all ${
                      error
                        ? "border-red-500 shake"
                        : "border-[rgb(63,63,63)] focus:border-[rgb(218,255,1)] focus:ring-[rgba(218,255,1,0.1)]"
                    }`}
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[rgb(218,255,1)] text-[rgb(17,17,19)] font-semibold text-sm tracking-[0.1em] h-12 rounded-xl hover:bg-[rgb(166,190,21)] hover:shadow-[0_0_30px_rgba(218,255,1,0.4)] transition-all duration-300"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  <Unlock size={16} className="mr-2" />
                  AUTHENTICATE
                </Button>

                {error && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-red-400 text-xs tracking-[0.15em] flex items-center justify-center gap-2"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    <AlertTriangle size={12} />
                    ACCESS DENIED — INVALID CODE
                  </motion.p>
                )}
              </form>

              <Separator className="bg-[rgb(63,63,63)] my-8 w-full" />

              {/* Request Access */}
              {!showRequest ? (
                <button
                  onClick={() => setShowRequest(true)}
                  className="text-[rgb(161,161,170)] text-xs tracking-[0.15em] hover:text-[rgb(218,255,1)] transition-colors underline underline-offset-4"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Request Supervisor Access
                </button>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center"
                >
                  <p className="text-[rgb(161,161,170)] text-sm mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Contact A&R for vault credentials:
                  </p>
                  <a
                    href="mailto:leaver@itzgreenmusic.com?subject=SUPERVISOR%20VAULT%20ACCESS%20REQUEST&body=Company:%0AProject:%0ADeadline:"
                    className="text-[rgb(218,255,1)] text-sm font-medium hover:underline"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    leaver@itzgreenmusic.com
                  </a>
                </motion.div>
              )}
            </motion.div>
          ) : (
            /* ═══════════════ THE LISTENING ROOM (UNLOCKED) ═══════════════ */
            <motion.div
              key="unlocked"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Access Granted Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 sm:mb-10">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck size={18} className="text-[rgb(74,222,128)]" />
                    <span
                      className="text-[rgb(74,222,128)] text-[10px] sm:text-xs tracking-[0.25em] font-medium"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      ACCESS GRANTED — SUPERVISOR CLEARANCE
                    </span>
                  </div>

                  <h1
                    className="text-white text-2xl sm:text-3xl md:text-4xl font-bold mb-2"
                    style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.02em" }}
                  >
                    B2B SUPERVISOR <span className="text-[rgb(218,255,1)]">VAULT</span>
                  </h1>
                  <p
                    className="text-[rgb(161,161,170)] text-xs sm:text-sm leading-relaxed max-w-lg"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    100% Human-Produced assets organized by TV Use-Case. All tracks One-Stop Cleared (BMI/ASCAP/The MLC). Zero generative AI.
                  </p>
                </div>
              </div>

              {/* Legal Notice Bar - THE LIABILITY SHIELD */}
              <div className="bg-[rgba(74,222,128,0.08)] border border-[rgba(74,222,128,0.3)] rounded-xl px-4 sm:px-6 py-4 mb-6 flex items-start sm:items-center gap-3">
                <ShieldCheck size={20} className="text-[rgb(74,222,128)] flex-shrink-0 mt-0.5 sm:mt-0" />
                <div>
                  <p className="text-[rgb(74,222,128)] text-xs sm:text-sm font-bold mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                    ONE-STOP CLEARANCE GUARANTEE
                  </p>
                  <p className="text-[rgb(218,218,218)] text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Master & Publishing 100% controlled by ItzGreen Music. Single signature clears both sides.{" "}
                    <Link to="/clearance" className="text-[rgb(218,255,1)] hover:underline">View Indemnity Protocol →</Link>
                  </p>
                </div>
              </div>

              {/* Production Credit Banner */}
              <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.06)] rounded-xl px-4 sm:px-6 py-4 mb-10 sm:mb-12">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
                    <span className="text-[rgb(100,100,110)]">Executive Producer:</span>{" "}
                    <span className="text-white font-medium">Leaver Hall for ItzGreen Music</span>
                    <span className="text-[rgb(63,63,63)] mx-2">|</span>
                    <span className="text-[rgb(100,100,110)]">BMI Publisher</span>
                  </div>
                  <p className="text-[rgb(100,100,110)] text-[10px] tracking-[0.1em]" style={{ fontFamily: "'Inter', sans-serif" }}>
                    STRICTLY B2B • 100% HUMAN-COMPOSED
                  </p>
                </div>
              </div>

              {/* Catalog Sections */}
              {vaultCatalogs.map((catalog, index) => (
                <CatalogSection 
                  key={catalog.id} 
                  catalog={catalog} 
                  index={index}
                  activeTrackId={activeTrackId}
                  setActiveTrackId={setActiveTrackId}
                  globalAudioRef={globalAudioRef}
                />
              ))}

              <Separator className="bg-[rgb(63,63,63)] my-10" />

              {/* Footer */}
              <div className="text-center">
                <p className="text-[rgb(161,161,170)] text-xs tracking-[0.15em] mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                  For placement inquiries or custom scoring requests:
                </p>
                <a 
                  href="mailto:leaver@itzgreenmusic.com?subject=SYNC%20PLACEMENT%20INQUIRY" 
                  className="text-[rgb(218,255,1)] text-sm font-medium hover:underline"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  leaver@itzgreenmusic.com
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-8px); }
          75% { transform: translateX(8px); }
        }
        .shake { animation: shake 0.3s ease; }
      `}</style>
    </main>
  );
};

export default VaultPage;
